if (!customElements.get('custom-header-root')) {
  const SCROLL_THRESHOLD = 5;
  const MOUSE_LEAVE_TIMEOUT = 300;
  const FADE_DURATION = 600;
  const DESKTOP_QUERY = '(min-width: 1000px)';
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, reducedMotion() ? 0 : ms));

  customElements.define(
    'custom-header-root',
    class CustomHeaderRoot extends HTMLElement {
      connectedCallback() {
        this.header = this.querySelector('.custom-header');
        this.bar = this.querySelector('.custom-header__bar');
        this.section = this.closest('.shopify-section');
        this.overlay = this.querySelector('.custom-header__overlay');
        this.panelsWrap = this.querySelector('[data-panels]');
        this.panels = Array.from(this.querySelectorAll('[data-panel]'));
        this.triggers = Array.from(this.querySelectorAll('[data-panel-trigger]'));
        this.toggle = this.querySelector('.custom-header__toggle');
        this.mobileMenu = this.querySelector('.custom-header__mobile-menu');
        this.region = this.querySelector('.custom-region');
        this.desktopQuery = window.matchMedia(DESKTOP_QUERY);

        this.fill = this.dataset.fill === 'true';
        this.invert = this.dataset.invert === 'true';
        this.scrolled = false;
        this.openKey = null;
        this.mobileOpen = false;

        this.onScroll = this.onScroll.bind(this);
        this.onBreakpointChange = this.onBreakpointChange.bind(this);
        this.onBlockSelect = this.onBlockSelect.bind(this);
        this.onBlockDeselect = this.onBlockDeselect.bind(this);

        if (this.dataset.sticky === 'true') this.section?.classList.add('shopify-section-header-sticky');

        this.resizeObserver = new ResizeObserver(() => this.setHeight());
        this.resizeObserver.observe(this.bar);

        window.addEventListener('scroll', this.onScroll, { passive: true });
        this.desktopQuery.addEventListener('change', this.onBreakpointChange);
        document.addEventListener('shopify:block:select', this.onBlockSelect);
        document.addEventListener('shopify:block:deselect', this.onBlockDeselect);

        this.initDesktop();
        this.initMega();
        this.initMobile();
        this.initRegion();
        this.onScroll();
        this.render();
        this.setHeight();
      }

      disconnectedCallback() {
        this.resizeObserver?.disconnect();
        window.removeEventListener('scroll', this.onScroll);
        this.desktopQuery?.removeEventListener('change', this.onBreakpointChange);
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
        document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
        document.documentElement.style.removeProperty('overflow');
      }

      // ---------- Visual state ----------
      render() {
        const desktopOpen = this.openKey !== null;
        this.header.classList.toggle('is-scrolled', (this.scrolled || this.fill) && !desktopOpen);
        this.header.classList.toggle('is-menu', desktopOpen || this.mobileOpen);
        this.header.classList.toggle('is-invert', this.invert && !(desktopOpen || this.mobileOpen || this.scrolled));
        this.bar.classList.toggle('is-compact', this.scrolled && !desktopOpen);
        this.overlay?.classList.toggle('is-open', desktopOpen);
        this.triggers.forEach((trigger) => {
          const active = trigger.dataset.panelTrigger === this.openKey;
          trigger.classList.toggle('is-active', active);
          trigger.setAttribute('aria-expanded', active ? 'true' : 'false');
        });
      }

      setHeight() {
        const height = `${Math.round(this.bar.getBoundingClientRect().height)}px`;
        document.documentElement.style.setProperty('--custom-header-height', height);
        document.documentElement.style.setProperty('--header-height', height);
      }

      onScroll() {
        const scrolled = window.scrollY >= SCROLL_THRESHOLD;
        if (scrolled !== this.scrolled) {
          this.scrolled = scrolled;
          this.render();
        }
        if (this.mobileOpen) this.setMobileMenuTop();
      }

      // ---------- Desktop panels ----------
      initDesktop() {
        this.header.addEventListener('mouseenter', () => clearTimeout(this.leaveTimer));
        this.header.addEventListener('mouseleave', () => {
          this.leaveTimer = setTimeout(() => this.closePanel(), MOUSE_LEAVE_TIMEOUT);
        });

        this.triggers.forEach((trigger) => {
          const key = trigger.dataset.panelTrigger;
          trigger.addEventListener('pointerenter', (event) => {
            if (event.pointerType !== 'mouse' || !this.desktopQuery.matches) return;
            clearTimeout(this.leaveTimer);
            this.openPanel(key);
          });
          trigger.addEventListener('click', (event) => {
            if (trigger.tagName === 'BUTTON') {
              // Hovering opened the panel already, so a click closes it; keyboard users toggle.
              event.preventDefault();
              if (this.openKey === key) this.closePanel();
              else this.openPanel(key);
            } else {
              this.closePanel();
            }
          });
          trigger.addEventListener('keydown', (event) => {
            if (event.key !== 'ArrowDown') return;
            event.preventDefault();
            this.openPanel(key).then(() => {
              this.panelFor(key)?.querySelector('a, button')?.focus();
            });
          });
        });

        // Other menu links close nothing on hover, matching the reference; they close on click.
        this.querySelectorAll('.custom-header__item--desktop a:not([data-panel-trigger])').forEach((link) => {
          link.addEventListener('click', () => this.closePanel());
        });

        this.addEventListener('keydown', (event) => {
          if (event.key !== 'Escape') return;
          if (this.openKey !== null) {
            const trigger = this.triggers.find((item) => item.dataset.panelTrigger === this.openKey);
            this.closePanel();
            trigger?.focus();
          } else if (this.mobileOpen) {
            this.setMobileOpen(false);
            this.toggle.focus();
          }
        });

        this.header.addEventListener('focusout', (event) => {
          if (event.relatedTarget && !this.header.contains(event.relatedTarget)) this.closePanel();
        });
      }

      panelFor(key) {
        return this.panels.find((panel) => panel.dataset.panel === key);
      }

      async openPanel(key) {
        const panel = this.panelFor(key);
        clearTimeout(this.leaveTimer);
        if (!panel || !this.panelsWrap || this.openKey === key) return;
        const previousKey = this.openKey;
        this.openKey = key;
        this.render();

        if (previousKey === null) {
          this.panels.forEach((item) => (item.hidden = item !== panel));
          panel.classList.remove('is-fading');
          this.animateHeight();
          requestAnimationFrame(() => this.panelsWrap.classList.add('is-open'));
          return;
        }

        // Switching panels: fade the current one out, then the next one in ("wait" mode).
        const previous = this.panelFor(previousKey);
        this.pendingKey = key;
        previous?.classList.add('is-fading');
        await wait(FADE_DURATION);
        if (this.pendingKey !== key || this.openKey !== key) return;
        this.panels.forEach((item) => (item.hidden = item !== panel));
        panel.classList.add('is-fading');
        this.animateHeight();
        requestAnimationFrame(() => panel.classList.remove('is-fading'));
      }

      closePanel() {
        if (this.openKey === null) return;
        this.openKey = null;
        this.pendingKey = null;
        this.render();
        this.panelsWrap.classList.remove('is-open');
        this.animateHeight(0);
      }

      animateHeight(target) {
        const wrap = this.panelsWrap;
        const from = wrap.getBoundingClientRect().height;
        const to = target === 0 ? 0 : wrap.firstElementChild.getBoundingClientRect().height;
        clearTimeout(this.heightTimer);
        wrap.style.height = `${from}px`;
        wrap.getBoundingClientRect();
        wrap.style.height = `${to}px`;
        this.heightTimer = setTimeout(
          () => {
            if (to === 0) {
              if (this.openKey === null) this.panels.forEach((panel) => (panel.hidden = true));
            } else if (this.openKey !== null) {
              wrap.style.height = 'auto';
            }
          },
          reducedMotion() ? 0 : FADE_DURATION,
        );
      }

      // ---------- Mega menu contents ----------
      initMega() {
        this.querySelectorAll('.custom-mega').forEach((mega) => {
          const buttons = Array.from(mega.querySelectorAll('[data-category]'));
          const lists = Array.from(mega.querySelectorAll('[data-category-links]'));
          const feature = mega.querySelector('[data-feature]');
          let active = null;

          const selectCategory = async (index) => {
            const next = active === index ? null : index;
            const current = lists.find((list) => list.dataset.categoryLinks === active);
            active = next;
            buttons.forEach((button) =>
              button.setAttribute('aria-expanded', button.dataset.category === next ? 'true' : 'false'),
            );
            this.swapFeature(feature, null);
            if (current) {
              current.classList.add('is-fading');
              await wait(FADE_DURATION);
              if (active !== next) return;
              current.hidden = true;
            }
            const target = lists.find((list) => list.dataset.categoryLinks === next);
            if (target) {
              target.classList.add('is-fading');
              target.hidden = false;
              requestAnimationFrame(() => requestAnimationFrame(() => target.classList.remove('is-fading')));
            }
            this.animateHeightIfOpen();
          };

          buttons.forEach((button) => button.addEventListener('click', () => selectCategory(button.dataset.category)));

          mega.querySelectorAll('.custom-mega__link[data-image-src]').forEach((link) => {
            link.addEventListener('mouseenter', () => this.swapFeature(feature, link));
            link.addEventListener('focus', () => this.swapFeature(feature, link));
          });
        });
      }

      async swapFeature(feature, link) {
        if (!feature) return;
        const source = link
          ? {
              src: link.dataset.imageSrc,
              srcset: link.dataset.imageSrcset,
              alt: link.dataset.imageAlt,
              label: link.dataset.imageLabel,
              url: link.href,
            }
          : {
              src: feature.dataset.defaultSrc,
              srcset: feature.dataset.defaultSrcset,
              alt: feature.dataset.defaultAlt,
              label: feature.dataset.defaultLabel,
              url: feature.dataset.defaultUrl,
            };
        if (feature.dataset.current === source.src) return;
        feature.dataset.current = source.src;
        feature.classList.add('is-fading');
        await wait(FADE_DURATION);
        if (feature.dataset.current !== source.src) return;
        const image = feature.querySelector('img');
        const caption = feature.querySelector('[data-feature-link]');
        if (image) {
          image.srcset = source.srcset || '';
          image.src = source.src;
          image.alt = source.alt || '';
        }
        if (caption) {
          caption.textContent = source.label || '';
          caption.href = source.url;
        }
        feature.classList.remove('is-fading');
      }

      animateHeightIfOpen() {
        if (this.openKey !== null && this.panelsWrap) this.animateHeight();
      }

      // ---------- Mobile menu ----------
      initMobile() {
        if (!this.toggle || !this.mobileMenu) return;
        this.toggle.addEventListener('click', () => this.setMobileOpen(!this.mobileOpen));

        const triggers = Array.from(this.mobileMenu.querySelectorAll('.custom-mrow__trigger'));
        triggers.forEach((trigger) => {
          trigger.addEventListener('click', () => {
            const open = trigger.getAttribute('aria-expanded') !== 'true';
            triggers.forEach((other) => {
              const isTarget = other === trigger && open;
              other.setAttribute('aria-expanded', isTarget ? 'true' : 'false');
              document.getElementById(other.getAttribute('aria-controls'))?.classList.toggle('is-open', isTarget);
            });
          });
        });
      }

      setMobileMenuTop() {
        const bottom = Math.max(0, Math.round(this.bar.getBoundingClientRect().bottom));
        this.mobileMenu.style.setProperty('--chr-menu-top', `${bottom}px`);
      }

      setMobileOpen(open) {
        if (this.mobileOpen === open) return;
        this.mobileOpen = open;
        this.toggle.classList.add('was-opened');
        this.toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        clearTimeout(this.mobileTimer);
        if (open) {
          this.setMobileMenuTop();
          this.mobileMenu.hidden = false;
          requestAnimationFrame(() => this.mobileMenu.classList.add('is-open'));
          document.documentElement.style.overflow = 'hidden';
        } else {
          this.mobileMenu.classList.remove('is-open');
          document.documentElement.style.removeProperty('overflow');
          this.mobileTimer = setTimeout(() => (this.mobileMenu.hidden = true), reducedMotion() ? 0 : 300);
        }
        this.render();
      }

      // ---------- Region dialog ----------
      initRegion() {
        if (!this.region) return;
        this.regionTriggers = Array.from(this.querySelectorAll('[data-region-open]'));
        this.regionTriggers.forEach((trigger) =>
          trigger.addEventListener('click', () => {
            this.lastRegionTrigger = trigger;
            this.openRegion();
          }),
        );
        this.region.querySelector('[data-region-close]')?.addEventListener('click', () => this.closeRegion());
        this.region.addEventListener('click', (event) => {
          if (event.target === this.region) this.closeRegion();
        });
        this.region.addEventListener('cancel', (event) => {
          event.preventDefault();
          this.closeRegion();
        });
      }

      openRegion() {
        this.region.showModal();
        this.regionTriggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'true'));
        requestAnimationFrame(() => this.region.classList.add('is-open'));
      }

      closeRegion() {
        this.region.classList.remove('is-open');
        this.regionTriggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
        setTimeout(
          () => {
            this.region.close();
            this.lastRegionTrigger?.focus();
          },
          reducedMotion() ? 0 : 200,
        );
      }

      // ---------- Breakpoints and theme editor ----------
      onBreakpointChange() {
        if (this.desktopQuery.matches) this.setMobileOpen(false);
        else this.closePanel();
      }

      onBlockSelect(event) {
        const target = this.panels.find((panel) => panel === event.target || panel.contains(event.target));
        if (target && this.desktopQuery.matches) this.openPanel(target.dataset.panel);
      }

      onBlockDeselect(event) {
        if (this.contains(event.target)) this.closePanel();
      }
    },
  );
}
